'use client';

// import { CalendarClock, Sparkles } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { ContactForm } from '@/components/ContactForm';
import {
	Dialog,
	DialogContent,
	DialogTitle,
	DialogTrigger,
} from '@/components/ui/dialog';
import { components } from '@/types/strapi';

export interface IncomingCTA {
	id: number;
	color: string;
	hover_color: string;
	border_color: string;
	dot_color: string;
	action: string | null;
	cta: {
		id: number;
		text: string;
		URL: string | null;
		target: string;
		variant: string;
		popup?: components['schemas']['SharedButtonComponent']['popup'];
	};
	icon: components['schemas']['Logo']['image'];
	mime: string;
	size: number;
	url: string;
	provider: string;
}

// Button content component
const StickyButton = memo(
	({
		cta,
		position,
		isVisible,
		isMobile,
		animationDelay,
		onClick,
	}: {
		cta: IncomingCTA;
		position: number;
		isVisible: boolean;
		isMobile: boolean;
		animationDelay: number;
		onClick?: () => void;
	}) => {
		const icon = useMemo(() => {
			return (
				cta.icon &&
				cta.icon.url && (
					// <CalendarClock
					// 	className="mb-1 h-3.5 w-3.5 opacity-80 transition-all duration-300 group-hover:opacity-100"
					// 	strokeWidth={1.5}
					// />
					<Image
						src={cta.icon.url}
						alt={cta.icon.name || 'sticky cta icon'}
						width={20}
						height={20}
						className="mb-1 h-3.5 w-3.5 opacity-80 transition-all duration-300 group-hover:opacity-100"
					/>
				)
			);
		}, [cta.icon, cta.cta.text]);

		// Get colors based on variant
		const { color, hoverColor, borderColor, dotColor } = useMemo(() => {
			if (
				cta.color &&
				cta.hover_color &&
				cta.border_color &&
				cta.dot_color
			) {
				return {
					color: cta.color,
					hoverColor: cta.hover_color,
					borderColor: cta.border_color,
					dotColor: cta.dot_color,
				};
			}

			switch (cta.cta.variant) {
				case 'primary':
					return {
						color: '#4262FF',
						hoverColor: '#3451DB',
						borderColor: 'border-blue-400/20',
						dotColor: 'bg-blue-300',
					};
				case 'outline':
					return {
						color: '#ffffff',
						hoverColor: '#f8f8f8',
						borderColor: 'border-gray-400/20',
						dotColor: 'bg-gray-300',
					};
				case 'muted':
					return {
						color: '#6B7280',
						hoverColor: '#4B5563',
						borderColor: 'border-gray-400/20',
						dotColor: 'bg-gray-300',
					};
				case 'simple':
				default:
					return {
						color: '#E0A80C',
						hoverColor: '#c99509',
						borderColor: 'border-amber-400/20',
						dotColor: 'bg-amber-300',
					};
			}
		}, [
			cta.color,
			cta.hover_color,
			cta.border_color,
			cta.dot_color,
			cta.cta.variant,
		]);

		// Simplified style calculation
		const buttonStyle = useMemo(
			() => ({
				top: `${position}vh`,
				'--bg-color': color,
				'--hover-bg-color': hoverColor,
				color:
					color === '#E0A80C' || color === '#ffffff'
						? 'black'
						: 'white',
				padding: isMobile
					? '0.25rem 0.25rem 0.5rem 0.25rem'
					: '0.25rem 0.25rem 0.75rem 0.25rem',
				transitionDelay: `${animationDelay}ms`,
			}),
			[position, color, hoverColor, isMobile, animationDelay],
		) as React.CSSProperties;

		return (
			<div
				className={`group fixed right-0 z-50 flex cursor-pointer flex-col items-center justify-center rounded-l-md border-b border-l border-t ${borderColor} bg-[var(--bg-color)] text-white shadow-md transition-all duration-300 ease-in-out hover:-translate-x-0.5 hover:bg-[var(--hover-bg-color)] hover:shadow-lg ${isVisible ? 'translate-x-0' : 'translate-x-full'}`}
				style={buttonStyle}
				onClick={onClick}
				role="button"
				tabIndex={0}>
				<div className="flex rotate-180 items-center gap-1 p-0 [writing-mode:vertical-lr]">
					<div className="relative transition-transform duration-300 group-hover:scale-110">
						{icon}
						<span
							className={`absolute right-0 top-0 h-1.5 w-1.5 rounded-full ${dotColor} opacity-0 transition-opacity duration-500 group-hover:animate-ping group-hover:opacity-100`}></span>
					</div>
					<span className="text-xs font-medium tracking-wide transition-all duration-300 group-hover:font-semibold">
						{cta.cta.text}
					</span>
				</div>

				{/* Tooltip - now using pure CSS hover */}
				<div className="pointer-events-none absolute right-full top-1/2 mr-2 -translate-y-1/2 whitespace-nowrap rounded bg-gray-800 px-2 py-1 text-xs text-white opacity-0 transition-opacity duration-200 group-hover:opacity-90">
					{cta.cta.text}
				</div>
			</div>
		);
	},
);

StickyButton.displayName = 'StickyButton';

// Link CTA component
const LinkCTA = memo(
	({
		cta,
		position,
		isVisible,
		isMobile,
		animationDelay,
	}: {
		cta: IncomingCTA;
		position: number;
		isVisible: boolean;
		isMobile: boolean;
		animationDelay: number;
	}) => {
		const url = cta.cta.URL === '#pricing' ? '/pricing' : cta.cta.URL;

		const handleClick = useCallback(
			(e: React.MouseEvent<HTMLAnchorElement>) => {
				if (!url) return;

				// Check if the URL has a hash
				const hashMatch = url.match(/#[^?]*/);
				if (hashMatch) {
					e.preventDefault();
					const hash = hashMatch[0];
					const element = document.querySelector(hash);
					if (element) {
						element.scrollIntoView({ behavior: 'smooth' });
						// Update URL without triggering a page reload
						window.history.pushState({}, '', url);
					} else {
						window.history.pushState({}, '', '/');
					}
				}
			},
			[url],
		);

		return (
			<Link
				href={url || '#'}
				target={cta.cta.target || '_self'}
				onClick={handleClick}
				passHref>
				<StickyButton
					cta={cta}
					position={position}
					isVisible={isVisible}
					isMobile={isMobile}
					animationDelay={animationDelay}
				/>
			</Link>
		);
	},
);

LinkCTA.displayName = 'LinkCTA';

// Dialog CTA component
const DialogCTA = memo(
	({
		cta,
		position,
		isVisible,
		isMobile,
		animationDelay,
		isOpen,
		onOpenChange,
	}: {
		cta: IncomingCTA;
		position: number;
		isVisible: boolean;
		isMobile: boolean;
		animationDelay: number;
		isOpen: boolean;
		onOpenChange: (open: boolean) => void;
	}) => {
		return (
			<Dialog
				open={isOpen}
				onOpenChange={onOpenChange}
				data-dialog-id={cta.id}>
				<DialogTrigger asChild>
					<StickyButton
						cta={cta}
						position={position}
						isVisible={isVisible}
						isMobile={isMobile}
						animationDelay={animationDelay}
					/>
				</DialogTrigger>
				{isOpen && (
					<DialogContent className="m-0 p-0">
						<DialogTitle className="sr-only">
							{cta.cta.text}
						</DialogTitle>
						<ContactForm script={cta.cta.popup?.externalScript} />
					</DialogContent>
				)}
			</Dialog>
		);
	},
);

DialogCTA.displayName = 'DialogCTA';

const StickyCTAs = memo(({ CTAs }: { CTAs: IncomingCTA[] }) => {
	const [openDialog, setOpenDialog] = useState<number | null>(null);
	const [isMobile, setIsMobile] = useState(false);
	const [isVisible, setIsVisible] = useState(false);
	const [windowHeight, setWindowHeight] = useState(0);

	// Detect viewport size and set window height
	useEffect(() => {
		const checkIfMobile = () => {
			setIsMobile(window.innerWidth < 768);
			setWindowHeight(window.innerHeight);
		};

		// Initial check
		checkIfMobile();

		// Add event listener
		window.addEventListener('resize', checkIfMobile);

		// Cleanup
		return () => window.removeEventListener('resize', checkIfMobile);
	}, []);

	// Animation on mount
	useEffect(() => {
		// Delay the appearance for a smoother page load experience
		const timer = setTimeout(() => {
			setIsVisible(true);
		}, 800);

		return () => clearTimeout(timer);
	}, []);

	const calculatePosition = useCallback(
		(index: number) => {
			// Calculate position based on viewport size and number of buttons
			const totalButtons = CTAs.length;
			const buttonHeight = isMobile ? 80 : 100; // Estimated button height in pixels
			const spacing = isMobile ? 60 : 50; // Spacing between buttons in pixels

			// Calculate vertical position - safely handle window access
			let position = 40 + index * 15; // Default fallback position

			if (typeof window !== 'undefined' && windowHeight > 0) {
				// For desktop: center the group of buttons and space them evenly
				// For mobile: position them closer together
				const basePosition =
					50 -
					((totalButtons * buttonHeight +
						(totalButtons - 1) * spacing) /
						2 /
						windowHeight) *
						100;
				position =
					basePosition +
					((index * (buttonHeight + spacing)) / windowHeight) * 100;
			}

			return position;
		},
		[isMobile, CTAs.length, windowHeight],
	);

	// Memoize the rendered CTAs to prevent unnecessary re-renders
	const renderedCTAs = useMemo(() => {
		return CTAs.map((cta, index) => {
			const position = calculatePosition(index);
			const animationDelay = 100 + index * 150;

			const action = cta.cta.URL ? 'link' : 'dialog';
			if (action === 'link' && cta.cta.URL) {
				return (
					<LinkCTA
						key={`link-${cta.id}`}
						cta={cta}
						position={position}
						isVisible={isVisible}
						isMobile={isMobile}
						animationDelay={animationDelay}
					/>
				);
			} else if (action === 'dialog') {
				return (
					<DialogCTA
						key={`dialog-${cta.id}`}
						cta={cta}
						position={position}
						isVisible={isVisible}
						isMobile={isMobile}
						animationDelay={animationDelay}
						isOpen={openDialog === cta.id}
						onOpenChange={open =>
							setOpenDialog(open ? cta.id : null)
						}
					/>
				);
			}

			return null;
		});
	}, [
		CTAs,
		calculatePosition,
		isVisible,
		isMobile,
		openDialog,
		setOpenDialog,
	]);

	return <div>{renderedCTAs}</div>;
});

StickyCTAs.displayName = 'StickyCTAs';

export default StickyCTAs;
